import React from 'react';
import { Box } from '@mui/material';
import { PdfThumbnail } from '../../thumbnails/pdfThumbnail';
import { ThumbnailLoadingOverlay } from '../../thumbnails/common';
import { WordThumbnail } from '../../thumbnails/wordThumbnail';
import { ExcelThumbnail } from '../../thumbnails/excelThumbnail';
import { GeneratedExtensionIcon } from '../../../gallery/fileExtensions/generatedExtensionIcon';
import type { DisplayedFile } from '../../../gallery/filesStore';
import { fileKindService } from 'services/fileService';
import { isPersistedSource } from 'shared/utils/sourceUrl';
import { listImageIconContainerSx } from '../../styles';
import { ListItemFrame, type ListItemFrameProps } from './ListItemFrame';

interface GenericListItemProps extends ListItemFrameProps {
  image: DisplayedFile;
  lazy?: boolean;
}

/** A tile of a file that is neither an image nor a video: a PDF, Word or Excel preview, or the extension's icon. */
export const GenericListItem: React.FC<GenericListItemProps> = ({ image, lazy = false, ...frameProps }) => {
  const { thumbnailSize, onClick } = frameProps;
  const isPdfNotConverted =
    fileKindService.isPdf(image) && !fileKindService.isConvertedPdf(image);
  const pdfThumbnailModel = isPdfNotConverted
    ? fileKindService.resolvePdfThumbnailModel(image)
    : null;
  const isWord = fileKindService.isWord(image);
  const isExcel = fileKindService.isExcel(image);

  return (
    <ListItemFrame {...frameProps}>
      {pdfThumbnailModel?.kind === 'ready' ? (
        <PdfThumbnail
          src={pdfThumbnailModel.src}
          thumbnailSize={thumbnailSize}
          onClick={onClick}
          fileType={image.type}
          fileExtension={image.data?.extension}
          lazy={lazy}
          thumbnailCacheKey={pdfThumbnailModel.cacheKey}
        />
      ) : pdfThumbnailModel?.kind === 'loading' ? (
        <Box onClick={onClick} sx={listImageIconContainerSx}>
          <ThumbnailLoadingOverlay />
        </Box>
      ) : isWord && isPersistedSource(image.downloadSrc) ? (
        <WordThumbnail
          src={image.downloadSrc}
          thumbnailSize={thumbnailSize}
          onClick={onClick}
          fileType={image.type}
          fileExtension={image.data?.extension}
        />
      ) : isExcel && isPersistedSource(image.downloadSrc) ? (
        <ExcelThumbnail
          src={image.downloadSrc}
          thumbnailSize={thumbnailSize}
          onClick={onClick}
          fileType={image.type}
          fileExtension={image.data?.extension}
        />
      ) : (
        <Box onClick={onClick} sx={listImageIconContainerSx}>
          <GeneratedExtensionIcon
            fileType={image.type}
            fileExtension={image.data?.extension}
            size={48}
          />
        </Box>
      )}
    </ListItemFrame>
  );
};
